import React, { useState, useEffect, useContext } from "react"
import { graphql } from "gatsby"
import Swiper from "react-id-swiper"
import axios from "axios"
import { Helmet } from "react-helmet"
import qs from "qs"

import { AppContext } from "../components/Context"
import SEO from "../components/SEO"
import Image from "../components/Image"
import Price from "../components/Price"
import Swatches from "../components/Swatches"
import FavoriteIcon from "../components/FavoriteIcon"
import AddToCartModal from "../components/AddToCart"
import PdpYMAL from "../components/PdpYMAL"
import RichText from "../components/RichText"
import RecentlyViewed, { saveProductToRV } from "../components/RecentlyViewed"

import { convertMmToInches, trackEvent, stripHtml } from "../helpers"
import cart from "../helpers/cart"

import styles from "./product.module.scss"
import "./product.scss"
import formStyles from "../components/emailCapture.module.scss"

const Product = ({ data, location }) => {
  const appState = useContext(AppContext)
  const {
    productStyle,
    contentfulTextBlock,
  } = data

  const {
    title,
    skus,
    product_id,
    description,
    relatedStyles,
    productType,
    story_body,
    recommendations,
  } = productStyle

  const [urgencyAmount, setUrgencyAmount] = useState(null)

  const parsedQuery = qs.parse(location.search, { ignoreQueryPrefix: true })
  const queryColor = parsedQuery.color || ""

  const defaultStyle = productStyle,
    defaultSku = skus.find(sku => sku.color.toLowerCase() === queryColor.toLowerCase()) || skus.find(sku => sku.images.swatch) || skus[0],
    defaultVariant = defaultSku.variants.find(v => v.size === appState.selectedSize) || defaultSku.variants.sort((a, b) => Number(a.size) - Number(b.size))[0]

  const [selectedStyle, setSelectedStyle] = useState(defaultStyle)
  const [selectedSku, setSelectedSku] = useState(defaultSku)
  const [selectedVariant, setSelectedVariant] = useState(defaultVariant)
  const [selectedSize, setSelectedSize] = useState(appState.selectedSize || -1)

  const [sizeSelectError, setSizeSelectError] = useState(null)

  // Images
  const [mainImageSwiper, getMainImageSwiper] = useState(null)
  const [thumbnailSwiper, getThumbnailSwiper] = useState(null)
  const [mobileImageSwiper, getMobileImageSwiper] = useState(null)
  const imageSwiperParams = {
    WrapperEl: "ol",
    threshold: 10,
    watchOverflow: true,
    preloadImage: true,
    updateOnImagesReady: true,
  }
  const mainImageSwiperParams = {
    ...imageSwiperParams,
    getSwiper: getMainImageSwiper,
    containerClass: `${styles.imageSwiperContainer}`,
    slidesPerView: 1,
    effect: "fade",
    loop: false,
    fadeEffect: {
      crossFade: true,
    },
    // pagination: {
    //   el: ".swiper-pagination",
    //   clickable: true,
    // },
  }
  const thumbnailSwiperParams = {
    ...imageSwiperParams,
    getSwiper: getThumbnailSwiper,
    containerClass: `${styles.thumbSwiperContainer}`,
    wrapperClass: `${styles.swiperWrapper}`,
    slideClass: `${styles.swiperSlide}`,
    slideActiveClass: `${styles.swiperSlideActive}`,
    centeredSlides: true,
    slidesPerView: "auto",
    touchRatio: 0.2,
    slideToClickedSlide: true,
    direction: "vertical",
  }
  const mobileImageSwiperParams = {
    ...imageSwiperParams,
    getSwiper: getMobileImageSwiper,
    containerClass: `${styles.imageSwiperContainer}`,
    slidesPerView: 1,
    loop: true,
    pagination: {
      el: ".swiper-pagination",
      clickable: true,
    },
  }

  const [allSkus, setAllSkus] = useState([])

  // Combine skus of related styles with skus of this style
  useEffect(() => {
    let updatedSkus = []
    skus.forEach(sku => {
      updatedSkus.push(sku)
    })
    relatedStyles.forEach(style => {
      style.skus.forEach(sku => {
        updatedSkus.push(sku)
      })
    })
    setAllSkus(updatedSkus)
  }, [skus, relatedStyles])

  // Save current product to user"s Recently Viewed
  useEffect(() => {
    saveProductToRV(productStyle)
  }, [productStyle])

  // Get up-to-date inventory
  useEffect(() => {
    const idQuery = relatedStyles.reduce((acc, cur) => (
      `${acc} OR id:${cur.product_id}`
    ), `id:${product_id}`)
    // get inventory from Shopify
    axios({
      url: "https://tamara-dev.myshopify.com/api/2020-04/graphql.json",
      method: "post",
      headers: {
        "X-Shopify-Storefront-Access-Token": "14ddd2b4c87fb6bc9c77e95cf3bed2c5",
      },
      data: {
        query: `
          query InventoryForSku {
            products(first: 10, query: "${idQuery}") {
              edges {
                node {
                  handle
                  variants(first: 100) {
                    edges {
                      node {
                        id
                        sku
                        quantityAvailable
                      }
                    }
                  }
                }
              }
            }
          }
        `,
      },
    })
    .then(result => {
      // Flatten variants from response for easier traversal
      let responseVariants = []
      result.data.data.products.edges.forEach(product => {
        product.node.variants.edges.forEach(variant => {
          responseVariants.push(variant.node)
        })
      })
      // Add inventory to skus
      allSkus.forEach(sku => {
        sku.variants.forEach(variant => {
          const upc = variant.upc
          const matchingVariant = responseVariants.find(v => upc === v.sku)
          if (matchingVariant)
            variant.quantity = matchingVariant.quantityAvailable
        })
      })
    })
  }, [product_id, relatedStyles, allSkus])

  // Connect thumbnail and main image swipers
  useEffect(() => {
    if (mainImageSwiper !== null && mainImageSwiper.controller &&
      thumbnailSwiper !== null && thumbnailSwiper.controller) {
      mainImageSwiper.controller.control = thumbnailSwiper
      thumbnailSwiper.controller.control = mainImageSwiper
    }
  }, [mainImageSwiper, thumbnailSwiper])

  // Initialize Affirm
  useEffect(() => {
    if (window.affirm && window.affirm.ui)
      window.affirm.ui.ready(() => {
        window.affirm.ui.refresh()
      })
  })

  // Get urgency data
  // TODO: Get this into the build process
  useEffect(() => {
    axios.get("https://l.tamaramellon.com/api/products/related", {
      params: {
        product_id: selectedStyle.product_id,
        size: "38",
        color: selectedSku.color,
        limit: "1",
        v: "2",
      },
    })
      .then(({ data }) => {
        setUrgencyAmount(data.views)
      })
  }, [selectedStyle, selectedSku])

  const updateSelectedSku = sku => {
    updateSelectedStyle(sku)
    setSelectedSku(sku)
    setSelectedVariant(sku.variants.find(v => v.size === selectedVariant.size))
    trackEvent("PDP", "Click", "Swatch")
  }

  useEffect(() => {
    if (typeof window !== "undefined" && window.outerWidth >= 1024) {
      if (mainImageSwiper !== null && thumbnailSwiper !== null) {
        mainImageSwiper.update()
        thumbnailSwiper.update()
        mainImageSwiper.slideTo(0, 0)
      }
    } else {
      if (mobileImageSwiper !== null) {
        mobileImageSwiper.update()
        mobileImageSwiper.slideTo(1, 0)
      }
    }
  }, [selectedSku, mainImageSwiper, thumbnailSwiper, mobileImageSwiper])

  const updateSelectedStyle = sku => {
    const style = relatedStyles.find(style => style.skus.find(s => s.id === sku.id)) || defaultStyle
    setSelectedStyle(style)
    if (typeof window !== "undefined") {
      const url = `/products/${style.handle}?color=${sku.color}${(parsedQuery.origin) ? `&origin=${parsedQuery.origin}` : ""}`
      window.history.replaceState({}, document.title, url)
    }
  }

  const handleSizeChange = e => {
    if (e.target.value !== "-1") {
      const variant = selectedSku.variants.find(v => v.size === e.target.value)
      setSelectedVariant(variant)
      setSelectedSize(variant.size)
      appState.set({
        selectedSize: variant.size
      })
      setSizeSelectError(null)
      if (typeof window !== "undefined")
        localStorage.setItem("selectedSize", variant.size)
      if (variant.quantity > 0)
        trackEvent("PDP", "Click", "In-stock Size")
      else if (variant.quantity === 0)
        trackEvent("PDP", "Click", "Out-of-stock Size")
    }
  }

  const handleGiftCardAmountChange = e => {
    const sku = allSkus.find(s => s.color === e.target.value)
    updateSelectedSku(sku)
  }

  const [showAddToCartModal, setShowAddToCartModal] = useState(false)

  const addToCart = () => {
    if (appState.selectedSize !== null || selectedStyle.productType === "Handbag") {
      const newItem = {
        productId: selectedStyle.product_id,
        variantId: selectedVariant.variant_id,
        quantity: 1,
        maxQuantity: selectedVariant.quantity,
        image: selectedSku.images.pdp.sort()[0],
        title: selectedStyle.title,
        price: parseInt(selectedVariant.price, 10),
        compareAtPrice: (selectedVariant.compareAtPrice) ?
          parseInt(selectedVariant.compareAtPrice, 10)
          : null,
        url: `${selectedStyle.url}?color=${selectedSku.color}`,
        color: selectedSku.color,
        size: selectedVariant.size,
        dateExpected: selectedVariant.dateExpected,
      }

      // Check if item is already added to cart
      const cartItem = cart.getCart().find(item => item.variantId === newItem.variantId)
      if (cartItem) {
        // Check if cart already has max quantity available
        if (cartItem.quantity === newItem.maxQuantity) {
          // Set error and return
          setSizeSelectError("All available items in this size are in your cart")
          return
        } else
          // Otherwise, increment quantity in cart by 1
          cart.incrementQuantity(newItem, 1)
      } else
        // Otherwise, add item to cart
        cart.addItem(newItem)

      appState.set({
        cartCount: cart.getCartCount(),
      })

      setShowAddToCartModal(true)

      addToCartAnalytics()
    }
  }

  const addToCartAnalytics = () => {
    // GA
    trackEvent("PDP", "Click", "Add to Cart")

    // Klaviyo
    if (window.hasOwnProperty("_learnq") && window.hasOwnProperty("klaviyoItem"))
      window._learnq.push(["track", "Add To Cart", window.klaviyoItem])

    // Pinterest
    if (window.hasOwnProperty("pintrk"))
      window.pintrk("track", "addtocart", {
        value: (parseInt(selectedVariant.price, 10) / 100).toFixed(2),
        order_quantity: 1,
        currency: "USD",
        product_id: selectedStyle.product_id,
      })

    // Custora
    if (window.hasOwnProperty("custoraTrack"))
      window.custoraTrack.addToCart(selectedVariant.variant_id, `$${selectedVariant.price}.00`, 1, `$${selectedVariant.compareAtPrice || "0"}.00`, selectedVariant.variant_id, title)

    // PebblePost
    window._pp.endUrl = "/tmaddbag"

    // Heap
    if (window.hasOwnProperty("heap"))
      window.heap.track("Add to Cart")
  }

  const [isImageZoomed, setIsImageZoomed] = useState(false)

  const handleImageClick = () => {
    if (window.outerWidth >= 1024)
      setIsImageZoomed(!isImageZoomed)
  }

  return(
    <>
      <SEO
        title={title}
        description={description}
        url={location.pathname}
      />

      <section className={styles.main}>
        <div className={(isImageZoomed) ? styles.imagesZoomed : styles.images}>
          {typeof window !== "undefined" && window.outerWidth >= 1024 &&
            <>
              <Swiper {...thumbnailSwiperParams}>
                {selectedSku.images.pdp.sort().map((image, i) => (
                  <li key={i}>
                    <Image
                      image={image}
                      altText={`${title} - ${selectedSku.color}`}
                      options={{
                        origin: "shopify",
                        width: "160",
                      }}
                      className={styles.thumbImage}
                    />
                  </li>
                ))}
                {selectedSku.videos.pdp.map((video, i) => (
                  <li key={`${i}vid`} className={styles.videoThumb}>
                    <Image
                      image={selectedSku.images.pdp[1]}
                      altText={`${title} - ${selectedSku.color}`}
                      options={{
                        origin: "shopify",
                        width: "160",
                      }}
                      className={styles.thumbImage}
                    />
                  </li>
                ))}
              </Swiper>
              <Swiper {...mainImageSwiperParams}>
                {selectedSku.images.pdp.sort().map((image, i) => (
                  <li key={i}>
                    <div
                      role="presentation"
                      onClick={handleImageClick}
                    >
                      <Image
                        image={image}
                        altText={`${title} - ${selectedSku.color}`}
                        options={{
                          origin: "shopify",
                          width: "2000",
                        }}
                        className={styles.mainImage}
                      />
                    </div>
                  </li>
                ))}
                {selectedSku.videos.pdp.map((video, i) => (
                  <li key={`${i}vid`}>
                    <video autoPlay loop muted playsInline className={styles.video}>
                      <source
                        src={video}
                        type="video/mp4" />
                    </video>
                  </li>
                ))}
              </Swiper>
            </>
          }
          {typeof window !== "undefined" && window.outerWidth < 1024 &&
            <Swiper {...mobileImageSwiperParams}>
              {selectedSku.images.pdp.sort().map((image, i) => (
                <li key={i}>
                  <Image
                    image={image}
                    altText={`${title} - ${selectedSku.color}`}
                    options={{
                      origin: "shopify",
                      width: "850",
                    }}
                    className={styles.mainImage}
                  />
                </li>
              ))}
              {selectedSku.videos.pdp.map((video, i) => (
                <li key={`${i}vid`}>
                  <video autoPlay loop muted playsInline className={styles.video}>
                    <source
                      src={video}
                      type="video/mp4" />
                  </video>
                </li>
              ))}
            </Swiper>
          }
          {urgencyAmount !== null && urgencyAmount >= 10 &&
            <p className={styles.urgency}>
              <strong>{urgencyAmount}</strong> people are shopping this item
            </p>
          }
        </div>
        <div className={styles.info}>
          <h1 className={styles.title}>{title.split(" - ")[0]}</h1>

          <p className={styles.price}>
            <Price
              price={selectedVariant.price}
              compareAtPrice={selectedVariant.compareAtPrice}
            />
          </p>

          {productType !== "Gift Card" &&
            <p className={`${styles.affirm} affirm-as-low-as`} data-page-type="product" data-affirm-type="text" data-amount={selectedVariant.price * 100}>Starting at 0% APR with Affirm.</p>
          }

          {productType === "Gift Card" ?
            <div className={styles.sizeContainer}>
              <h2>Select Amount</h2>
              <select
                className={styles.sizes}
                value={selectedSku.color}
                onChange={handleGiftCardAmountChange}
                onBlur={handleGiftCardAmountChange}
              >
                {selectedStyle.skus
                  .sort((a,b) => Number(a.color) - Number(b.color))
                  .map((sku, i) => (
                    <option
                      key={i}
                      value={sku.color}
                    >
                      ${sku.color}
                    </option>
                  ))}
              </select>
            </div>
            :
            <div className={styles.swatchContainer}>
              <h2>{selectedSku.color} {selectedSku.material}</h2>
              <Swatches
                skus={allSkus.sort((a, b) => [a.material, a.color] > [b.material, b.color] ? 1 : -1)}
                selectedSku={selectedSku}
                updateSelectedSku={updateSelectedSku}
                customStyles={styles}
              />
            </div>
          }

          {productType === "Footwear" &&
            <div className={styles.sizeContainer}>
              <h2>Select Size</h2>
              <select
                className={styles.sizes}
                value={selectedSize}
                onChange={handleSizeChange}
                onBlur={handleSizeChange}
              >
                <option value="-1" hidden>Choose your size</option>
                {selectedSku.variants
                  .sort((a, b) => Number(a.size) - Number(b.size))
                  .map((variant, i) => (
                    <option
                      key={i}
                      value={variant.size}
                    >
                      EU {variant.size} | US {variant.size - 30}
                      {(Number(variant.quantity) <= 0) ?
                        " (Out of Stock) - Notify Me"
                        : (Number(variant.quantity) <= 2) ?
                          ` - Only ${variant.quantity} left`
                          : ""
                      }
                    </option>
                  ))}
              </select>
            </div>
          }

          {sizeSelectError &&
          <p className={styles.sizeSelectError}>{sizeSelectError}</p>
          }

          {selectedSize === -1 ?
            <button
              type="button"
              className={styles.addToCartBtn}
              onClick={() => setSizeSelectError("Please select a size")}
            >
              Add to Cart
            </button>
            :
            <>
              {selectedVariant.quantity > 0 ?
                <button
                  type="button"
                  className={styles.addToCartBtn}
                  onClick={addToCart}
                >
                  Add to Cart
                </button>
                :
                <BIS
                  variant={selectedVariant}
                  style={selectedStyle}
                />
              }
            </>
          }

          <div className={styles.favoriteWrapper}>
            <FavoriteIcon
              sku={selectedSku.sku}
              style={selectedStyle}
              className={styles.favoriteBtn}
            />
          </div>

          {selectedVariant.dateExpected &&
            <p className={styles.dateExpected}>
              Expected ship date no later than {new Date(selectedVariant.dateExpected).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
            </p>
          }

          <ul className={styles.detailsContainer}>
            <li>
              <h4 className={styles.detailsHeading}>Free shipping & pickup returns</h4>
              <p className={styles.details}>We offer complimentary ground shipping on all U.S. orders and free scheduled pickups for all returns.</p>
            </li>
            <li>
              <h4 className={styles.detailsHeading}>2 year cobbler care</h4>
              <p className={styles.details}>Each pair of our shoes is guaranteed with repairs for up to two years with our Cobbler Concierge service.</p>
            </li>
          </ul>

          <PDPDetails
            heading="Product details"
            open={true}
          >
            {story_body !== "Unknown" &&
              <li>{stripHtml(story_body)}<br /><br /></li>
            }
            {selectedStyle.descriptionHtml.replace(/(<p>|<\/p>|<ul>|<\/ul>|<li>|<\/li>)/g,"").split("<br>").map((text, i) => (
              <li key={i}>{text}</li>
            ))}
          </PDPDetails>

          {selectedVariant.bootFitGuide.ankle !== 0 &&
            <PDPDetails
              heading="Size & Fit"
              open={false}
            >
              <li>Shaft Height: {`${convertMmToInches(selectedVariant.bootFitGuide.shaft)}" / ${selectedVariant.bootFitGuide.shaft}mm`}</li>
              <li>Calf Circumference: {`${convertMmToInches(selectedVariant.bootFitGuide.calf)}" / ${selectedVariant.bootFitGuide.calf}mm`}</li>
              <li>Ankle Circumference: {`${convertMmToInches(selectedVariant.bootFitGuide.ankle)}" / ${selectedVariant.bootFitGuide.ankle}mm`}</li>
            </PDPDetails>
          }
        </div>
      </section>

      {showAddToCartModal &&
        <AddToCartModal
          image={selectedSku.images.pdp[0]}
          title={selectedStyle.title}
          color={selectedSku.color}
          size={selectedVariant.size}
          price={parseInt(selectedVariant.price, 10)}
          compareAtPrice={(selectedVariant.compareAtPrice) ?
            parseInt(selectedVariant.compareAtPrice, 10)
            : null}
          setShowAddToCartModal={setShowAddToCartModal}
        />
      }

      {productType !== "Gift Card" &&
        <PdpYMAL
          // shopifyProductId={product_id}
          // color={selectedSku.color}
          // location={location}
          products={recommendations}
        />
      }

      <section className={styles.quote}>
        <RichText
          json={contentfulTextBlock.description.json}
        />
      </section>

      <RecentlyViewed
        colorway={selectedSku.color}
        shopifyProductId={product_id}
      />

      <Helmet>
        {/* Affirm */}
        <script async>{`
          var _affirm_config = {
            public_api_key: "XKI7FDVQ0VJSLQKZ",
            script: "https://cdn1.affirm.com/js/v2/affirm.js"
          };
          (function(l,g,m,e,a,f,b){var d,c=l[m]||{},h=document.createElement(f),n=document.getElementsByTagName(f)[0],k=function(a,b,c){return function(){a[b]._.push([c,arguments])}};c[e]=k(c,e,"set");d=c[e];c[a]={};c[a]._=[];d._=[];c[a][b]=k(c,a,b);a=0;for(b="set add save post open empty reset on off trigger ready setProduct".split(" ");a<b.length;a++)d[b[a]]=k(c,e,b[a]);a=0;for(b=["get","token","url","items"];a<b.length;a++)d[b[a]]=function(){};h.async=!0;h.src=g[f];n.parentNode.insertBefore(h,n);delete g[f];d(g);l[m]=c})(window,_affirm_config,"affirm","checkout","ui","script","ready");
        `}</script>

        {/* Back in Stock */}
        {/*<script src="https://app.backinstock.org/widget/5386_1578342855.js?v=5&shop=tamara-dev.myshopify.com" async />*/}

        {/* Microdata */}
        <script type="application/ld+json">{`
          [
            ${allSkus.filter(sku => sku.variants.length).map(sku => {
              return `{
                "@context": "https://schema.org/",
                "@type": "Product",
                "name": "${title} - ${sku.color}",
                "brand": "Tamara Mellon",
                "sku": "${sku.sku}",
                "url": "https://www.tamaramellon.com${productStyle.url}?color=${sku.color}",
                "description": "${description.replace(/"/g,"\\\"")}",
                "image": "${sku.images.pdp[0]}",
                "offers": {
                  "@type": "Offer",
                  "priceCurrency": "USD",
                  "price": "${sku.variants[0].price}.00"
                }
              }`
            })}
          ]
        `}</script>

        {/* Criteo */}
        <script type="text/javascript">{`
          window.criteo_q = window.criteo_q || [];
          window.criteo_q.push(
            { event: "viewItem", ecpplugin: "shopify", product: "${product_id}" }
          );
        `}</script>
        
        {/* Klaviyo */}
        <script>{`
          window.klaviyoItem = {
            Name: "${title}",
            ProductID: "${product_id}",
            Categories: [${productStyle.collections.map(c => `"${c}"`)}],
            ImageURL: "${defaultSku.images.pdp[0]}",
            URL: "https://www.tamaramellon.com${productStyle.url}?color=${defaultSku.color}",
            Brand: "Tamara Mellon",
            Price: "$${defaultVariant.price}.00",
            CompareAtPrice: "$${defaultVariant.compareAtPrice || "0"}.00"
          };
  
          window._learnq = window._learnq || [];
          
          window._learnq.push(["track", "Viewed Product", window.klaviyoItem]);
  
          window._learnq.push(["trackViewedItem", {
            Title: window.klaviyoItem.Name,
            ItemId: window.klaviyoItem.ProductID,
            Categories: window.klaviyoItem.Categories,
            ImageUrl: window.klaviyoItem.ImageURL,
            Url: window.klaviyoItem.URL,
            Metadata: {
              Brand: window.klaviyoItem.Brand,
              Price: window.klaviyoItem.Price,
              CompareAtPrice: window.klaviyoItem.CompareAtPrice
            }
          }]);
        `}</script>
      </Helmet>
    </>
  )
}

export default Product

export const pageQuery = graphql`
  query($id: String!) {
    productStyle(id: {eq: $id}) {
      ...ProductStyleFragment
      relatedStyles {
        ...ProductStyleFragment
      }
    }
    contentfulTextBlock(componentName: {eq: "PDP Quote Module"}) {
      description {
        json
      }
    }
  }
`

const PDPDetails = ({ heading, open, children }) => {
  const [isOpen, setIsOpen] = useState(open)

  return (
    <div className={styles.detailsContainer}>
      <h4 className={(isOpen) ? styles.detailsHeadingOpen : styles.detailsHeadingClosed}>
        <button
          onClick={() => setIsOpen(!isOpen)}
        >{heading}</button>
      </h4>
      <ul className={(isOpen) ? styles.detailsOpen : styles.detailsClosed}>
        {children}
      </ul>
    </div>
  )
}

const BIS = ({ variant, style }) => {
  const [email, setEmail] = useState("")
  const [success, setSuccess] = useState(false)
  const [errorMessage, setErrorMessage] = useState(null)

  const submitEmail = e => {
    e.preventDefault()

    // window.BIS.create(email, variant.variant_id, style.product_id)
    //   .then(response => {
    //     if (response.status === "OK" || response.errors.email[0].includes("Thank you")) {
    //       setSuccess(true)
    //       trackEvent("PDP", "Joined", "Waitlist")
    //     } else
    //       setErrorMessage(response.errors.email[0])
    //   })

    axios.get(`https://app.backinstock.org/stock_notification/create.json?shop=tamara-dev.myshopify.com&notification[email]=${email}&notification[product_no]=${style.product_id}&notification[quantity_required]=1&notification[accepts_marketing]=true&notification[customer_utc_offset]=${new Date().getTimezoneOffset() * 60}&variant[variant_no]=${variant.variant_id}`)
      .then(({ data }) => {
        if (data.status === "OK" || data.errors.email[0].includes("Thank you")) {
          setSuccess(true)
          trackEvent("PDP", "Joined", "Waitlist")
        } else
          setErrorMessage(data.errors.email[0])
      })
  }

  return (
    <div className={styles.bisContainer}>
      {success ?
        <>
          <h5>Thank you!</h5>
          <p>We'll let you know when this item is back in stock.</p>
        </>
      :
        <>
          {style.productType === "Footwear" ?
            <h5>EU {variant.size} | US {variant.size - 30}</h5>
            :
            <h5>{variant.size}</h5>
          }
          <p>We'll let you know when this item is back in stock.</p>
          <form
            className={`${formStyles.form} ${styles.bisForm}`}
            onSubmit={submitEmail}
          >
            <input
              type="email"
              placeholder="Your email"
              className={formStyles.email}
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
            />
            <input
              type="submit"
              value="Notify Me"
              className={formStyles.submit}
            />
          </form>
          {errorMessage &&
          <p className={`${formStyles.error} ${styles.bisError}`}>
            {errorMessage}
          </p>
          }
        </>
      }
    </div>
  )
}
